/**
 * Image Type Editor modal — create, rename, delete an image type and
 * manage its reference images: several per body style, one row per style.
 * This is a global/operator concern (the reference library is not
 * per-dealer), so it lives with the Photo AI (INVI HQ) page. Extracted from
 * the original Photo Configuration monolith so the shared reference UI has
 * a single home.
 */
import React, { useEffect, useMemo, useState } from "react"
import { Plus, X } from "lucide-react"

import {
  ALL_BODY_STYLES,
  BODY_STYLE_DISPLAY_NAMES,
  BodyStyle,
  CreateImageTypeRequest,
  ImageType,
  ImageTypeReferenceResponse,
  createImageType,
  deleteImageType,
  deleteImageTypeReference,
  updateImageType,
  uploadImageTypeReference,
} from "../../api/photoConfiguration"
import {
  ACCEPTED_IMAGE_TYPES,
  dangerButtonStyle,
  errorBannerStyle,
  fieldHintStyle,
  fieldLabelStyle,
  hiddenInputStyle,
  inputStyle,
  largeModalStyle,
  modalActionsRightStyle,
  modalActionsRowStyle,
  modalFieldStyle,
  modalOverlayStyle,
  modalTitleStyle,
  primaryButtonStyle,
  referenceAddTileStyle,
  referenceConfirmButtonStyle,
  referenceGridSectionStyle,
  referenceRemoveButtonStyle,
  referenceRemoveConfirmActionsStyle,
  referenceRemoveConfirmStyle,
  referenceRowCountStyle,
  referenceRowHeadStyle,
  referenceRowNameStyle,
  referenceRowStyle,
  referenceThumbnailImageStyle,
  referenceThumbnailStyle,
  referenceTileRowStyle,
  secondaryButtonStyle,
  slugDisplayStyle,
} from "./photoConfigShared"
import { THEME } from "../../config/theme"


interface ImageTypeEditorModalProps {
  imageType: ImageType | null
  onClose: () => void
  onSaved: () => void
}

export function ImageTypeEditorModal({ imageType, onClose, onSaved }: ImageTypeEditorModalProps) {
  const isEdit = imageType !== null
  // `initialName` is what the server has on record. We hold it locally
  // (not just on the imageType prop) so autosave can update it without
  // forcing a parent re-render — keeps the modal open and stable while
  // the user continues editing other fields like references.
  const [initialName, setInitialName] = useState<string>(imageType?.display_name ?? "")
  const [displayName, setDisplayName] = useState<string>(initialName)
  const [saving, setSaving] = useState<boolean>(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [references, setReferences] = useState(imageType?.references ?? [])

  useEffect(() => {
    setReferences(imageType?.references ?? [])
    setInitialName(imageType?.display_name ?? "")
    setDisplayName(imageType?.display_name ?? "")
  }, [imageType])

  // The server lists references oldest first; new uploads append, so each
  // body style's row keeps upload order.
  const referencesByBodyStyle = useMemo(() => {
    const map: Partial<Record<BodyStyle, ImageTypeReferenceResponse[]>> = {}
    for (const ref of references) {
      const forStyle = map[ref.body_style] ?? []
      forStyle.push(ref)
      map[ref.body_style] = forStyle
    }
    return map
  }, [references])

  // EDIT mode: rename autosaves on blur / Enter. No "Save Name" button —
  // the modal stays open after the save so the user can continue
  // working on references. The library list behind the modal refreshes
  // when the modal closes (`onClose` → parent loads fresh state).
  const handleNameAutosave = async () => {
    if (!isEdit || !imageType) return
    const trimmed = displayName.trim()
    if (!trimmed) {
      setModalError("Display name is required")
      return
    }
    if (trimmed === initialName) return
    setSaving(true)
    setModalError(null)
    try {
      await updateImageType(imageType.id, { display_name: trimmed })
      setInitialName(trimmed)
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Save failed")
    } finally {
      setSaving(false)
    }
  }

  // CREATE mode: explicit Create button. There's no row to autosave to
  // until the row exists, and the operator gets a clean two-step flow.
  const handleCreate = async () => {
    if (isEdit) return
    const trimmed = displayName.trim()
    if (!trimmed) {
      setModalError("Display name is required")
      return
    }
    setSaving(true)
    setModalError(null)
    try {
      const payload: CreateImageTypeRequest = { display_name: trimmed }
      await createImageType(payload)
      onSaved()
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteImageType = async () => {
    if (!isEdit || !imageType) return
    if (!window.confirm(
      `Delete "${imageType.display_name}"? Removes it from every bucket and discards all its references. Photos already tagged with "${imageType.slug}" keep their tag but it will no longer resolve.`,
    )) return
    setSaving(true)
    try {
      await deleteImageType(imageType.id)
      onSaved()
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Delete failed")
      setSaving(false)
    }
  }

  // Each file becomes its own reference. Uploaded one at a time, not all
  // at once: the backend computes each photo's CLIP embedding inline, and
  // a drop of thirty files in parallel would tie it up. One failed file
  // doesn't stop the rest — the banner names every file that failed.
  const handleUploadReferences = async (
    bodyStyle: BodyStyle,
    files: File[],
    onProgress: (uploadedCount: number) => void,
  ) => {
    if (!imageType) return
    setModalError(null)
    const failures: string[] = []
    for (let index = 0; index < files.length; index++) {
      const file = files[index]
      try {
        const uploaded = await uploadImageTypeReference(imageType.id, bodyStyle, file)
        // A photo already in this row comes back as the existing reference
        // (the server never stores it twice), so it isn't shown twice either.
        setReferences((current) =>
          current.some((r) => r.id === uploaded.id)
            ? current
            : [
                ...current,
                {
                  id: uploaded.id,
                  body_style: uploaded.body_style,
                  reference_url: uploaded.reference_url,
                  uploaded_at: uploaded.uploaded_at,
                },
              ],
        )
      } catch (err) {
        failures.push(`${file.name} (${err instanceof Error ? err.message : "upload failed"})`)
      }
      onProgress(index + 1)
    }
    if (failures.length > 0) {
      setModalError(
        `Could not upload ${failures.length} of ${files.length}: ${failures.join(", ")}`,
      )
    }
  }

  const handleDeleteReference = async (referenceId: number) => {
    if (!imageType) return
    setModalError(null)
    try {
      await deleteImageTypeReference(imageType.id, referenceId)
      setReferences((current) => current.filter((r) => r.id !== referenceId))
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Delete failed")
    }
  }

  // Done flushes any pending rename (the user might tab into the input,
  // edit, and click Done without blurring first) before closing.
  const handleDone = async () => {
    if (isEdit && displayName.trim() !== initialName) {
      await handleNameAutosave()
    }
    onClose()
  }

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={largeModalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={modalTitleStyle}>
          {isEdit ? `Edit: ${imageType?.display_name}` : "Add Image Type"}
        </h2>

        {modalError && <div style={errorBannerStyle}>{modalError}</div>}

        <div style={modalFieldStyle}>
          <label style={fieldLabelStyle}>Display Name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            onBlur={isEdit ? handleNameAutosave : undefined}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                if (isEdit) void handleNameAutosave()
                else void handleCreate()
              }
            }}
            style={inputStyle}
            placeholder="e.g. Passenger Rear Corner Exterior"
          />
          {isEdit && (
            <span style={fieldHintStyle}>
              Saves automatically when you click outside the field or press Enter.
            </span>
          )}
        </div>

        {isEdit && imageType && (
          <div style={modalFieldStyle}>
            <label style={fieldLabelStyle}>Slug</label>
            <div style={slugDisplayStyle}>{imageType.slug}</div>
          </div>
        )}

        {!isEdit && (
          <div style={modalActionsRowStyle}>
            <div></div>
            <div style={modalActionsRightStyle}>
              <button onClick={onClose} disabled={saving} style={secondaryButtonStyle}>
                Cancel
              </button>
              <button onClick={handleCreate} disabled={saving} style={primaryButtonStyle}>
                {saving ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        )}

        {isEdit && imageType && (
          <>
            <div style={referenceGridSectionStyle}>
              <span style={fieldLabelStyle}>References</span>
              <p style={fieldHintStyle}>
                Add several photos per body style. Every incoming photo is compared against
                all of them and the closest match wins — more variety means fewer wrong
                labels. Drop several photos at once. Uploads save automatically.
              </p>
              <div>
                {ALL_BODY_STYLES.map((bodyStyle) => (
                  <ReferenceRow
                    key={bodyStyle}
                    bodyStyle={bodyStyle}
                    references={referencesByBodyStyle[bodyStyle] ?? []}
                    onUpload={(files, onProgress) => handleUploadReferences(bodyStyle, files, onProgress)}
                    onDelete={handleDeleteReference}
                  />
                ))}
              </div>
            </div>

            <div style={modalActionsRowStyle}>
              <button onClick={handleDeleteImageType} disabled={saving} style={dangerButtonStyle}>
                Delete Image Type
              </button>
              <div style={modalActionsRightStyle}>
                <button onClick={handleDone} disabled={saving} style={primaryButtonStyle}>
                  Done
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}


function photoCountLabel(count: number): string {
  if (count === 0) return "No photos yet"
  return count === 1 ? "1 photo" : `${count} photos`
}


// One body style's references: name and count, every thumbnail, and an add
// tile that takes one or many files by drop or click.
function ReferenceRow({
  bodyStyle,
  references,
  onUpload,
  onDelete,
}: {
  bodyStyle: BodyStyle
  references: ImageTypeReferenceResponse[]
  onUpload: (files: File[], onProgress: (uploadedCount: number) => void) => Promise<void>
  onDelete: (referenceId: number) => Promise<void>
}) {
  const [dragHover, setDragHover] = useState<boolean>(false)
  // Non-null while a batch uploads: how many of how many are done.
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const busy = progress !== null

  const handleFiles = async (fileList: FileList | null) => {
    if (busy || !fileList) return
    const images = Array.from(fileList).filter((file) => file.type.startsWith("image/"))
    if (images.length === 0) return
    setProgress({ done: 0, total: images.length })
    try {
      await onUpload(images, (done) => setProgress({ done, total: images.length }))
    } finally {
      setProgress(null)
    }
  }

  return (
    <div style={referenceRowStyle}>
      <div style={referenceRowHeadStyle}>
        <span style={referenceRowNameStyle}>{BODY_STYLE_DISPLAY_NAMES[bodyStyle]}</span>
        <span style={referenceRowCountStyle}>{photoCountLabel(references.length)}</span>
      </div>
      <div style={referenceTileRowStyle}>
        {references.map((reference) => (
          <ReferenceThumbnail
            key={reference.id}
            reference={reference}
            onDelete={() => onDelete(reference.id)}
          />
        ))}
        <label
          style={referenceAddTileStyle(dragHover, busy)}
          onDragOver={(e) => { e.preventDefault(); setDragHover(true) }}
          onDragLeave={() => setDragHover(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragHover(false)
            void handleFiles(e.dataTransfer.files)
          }}
        >
          <input
            type="file"
            accept={ACCEPTED_IMAGE_TYPES}
            multiple
            disabled={busy}
            aria-label={`Add ${BODY_STYLE_DISPLAY_NAMES[bodyStyle]} reference photos`}
            onChange={(e) => {
              void handleFiles(e.target.files)
              // Clear it so picking the same file again still fires onChange.
              e.target.value = ""
            }}
            style={hiddenInputStyle}
          />
          {progress ? (
            <span>Uploading {Math.min(progress.done + 1, progress.total)} of {progress.total}…</span>
          ) : (
            <>
              <Plus size={20} strokeWidth={2} color={THEME.colors.navy} />
              <span>Add photos<br />drop or click</span>
            </>
          )}
        </label>
      </div>
    </div>
  )
}


// One reference photo. Its × asks "Remove this reference?" over the photo
// before anything is deleted.
function ReferenceThumbnail({
  reference,
  onDelete,
}: {
  reference: ImageTypeReferenceResponse
  onDelete: () => Promise<void>
}) {
  const [confirming, setConfirming] = useState<boolean>(false)
  const [removing, setRemoving] = useState<boolean>(false)

  const handleRemove = async () => {
    setRemoving(true)
    try {
      await onDelete()
    } finally {
      // On success this thumbnail unmounts; on failure it returns to normal
      // and the modal banner says why.
      setRemoving(false)
      setConfirming(false)
    }
  }

  return (
    <div style={referenceThumbnailStyle}>
      {/* The thumbnail is the full-size reference (~0.7 MB); lazy loading
          fetches only the rows on screen instead of every photo at once. */}
      <img
        src={reference.reference_url}
        alt={`${BODY_STYLE_DISPLAY_NAMES[reference.body_style]} reference`}
        loading="lazy"
        decoding="async"
        style={referenceThumbnailImageStyle}
      />
      {confirming ? (
        <div style={referenceRemoveConfirmStyle}>
          <span>{removing ? "Removing…" : "Remove this reference?"}</span>
          {!removing && (
            <div style={referenceRemoveConfirmActionsStyle}>
              <button onClick={() => void handleRemove()} style={referenceConfirmButtonStyle(true)}>
                Remove
              </button>
              <button onClick={() => setConfirming(false)} style={referenceConfirmButtonStyle(false)}>
                Keep
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          style={referenceRemoveButtonStyle}
          aria-label="Remove this reference"
          title="Remove this reference"
        >
          <X size={12} strokeWidth={2.5} />
        </button>
      )}
    </div>
  )
}
